/**
 * LayerManagerPanel — the Layer Properties Manager palette (Faz 5.4/5.14,
 * OCS layers.rs parity). Toolbar (New / Delete / Set Current / search /
 * Layer States) over a sortable column table: Status · Name (inline rename)
 * · On · Freeze · Lock · Plot · Color (AciColorPicker) · Linetype ·
 * Lineweight · Transparency, plus one per-viewport VPLAYER freeze column for
 * every viewport of the active paper-space layout (columns derive live from
 * useLayoutStore — the OCS sync_with_viewports pattern).
 *
 * 1000+ layers: simple windowing — fixed row height, only the visible slice
 * renders inside an absolutely positioned band over a full-height spacer;
 * the search box shrinks the set first.
 */

import {
    Box,
    Button,
    Flex,
    Icon,
    Input,
    NumberInput,
    NumberInputField,
    Select,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import {
    CloudSnow, Eye, EyeOff, Flag, Lock, Unlock, Plus, Printer, Trash2,
} from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore, type Layer } from '../../state/store';
import { useLayoutStore } from '../../state/useLayoutStore';
import { AciColorPicker } from './AciColorPicker';
import { LayerStatesDialog } from './LayerStatesDialog';
import { ConfirmDialog } from '../common/ConfirmDialog';

const ROW_H = 28;
const WINDOW_PAD = 6;

const LINETYPES = ['CONTINUOUS', 'DASHED', 'HIDDEN', 'CENTER', 'PHANTOM', 'DASHDOT', 'DOTTED'] as const;
const LINEWEIGHTS = [0, 0.05, 0.09, 0.13, 0.15, 0.18, 0.2, 0.25, 0.3, 0.35, 0.4, 0.5, 0.53, 0.6, 0.7, 0.8, 0.9, 1.0, 1.06, 1.2, 1.4, 1.58, 2.0, 2.11, 2.5, 3.0, 3.5];

type SortCol = 'name' | 'on' | 'freeze' | 'lock' | 'plot' | 'color' | 'linetype' | 'lineweight' | 'transparency';
interface SortState { col: SortCol; dir: 'asc' | 'desc' }

/** Column geometry shared by the header and the rows (name flexes). */
const COLS = { status: 26, on: 34, freeze: 34, lock: 34, plot: 34, color: 72, linetype: 108, lineweight: 92, transparency: 84, vp: 40 };

/** One toggle cell button (On/Freeze/Lock/Plot + VP freeze columns). */
function CellToggle({ w, on, label, children, onClick, tone }: {
    w: number;
    on: boolean;
    label: string;
    children: React.ReactNode;
    onClick: () => void;
    tone?: string;
}) {
    return (
        <Tooltip label={label} placement="bottom" openDelay={300} hasArrow>
            <Flex
                as="button"
                role="gridcell"
                aria-label={label}
                aria-pressed={on}
                align="center"
                justify="center"
                w={`${w}px`}
                h="full"
                flexShrink={0}
                color={on ? (tone ?? 'brand.500') : 'text.secondary'}
                opacity={on ? 1 : 0.4}
                _hover={{ bg: 'bg.hover', opacity: 1 }}
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); onClick(); }}
            >
                {children}
            </Flex>
        </Tooltip>
    );
}

export function LayerManagerPanel() {
    const { t } = useTranslation();
    const layers = useAppStore((s) => s.layers);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const entities = useAppStore((s) => s.entities);
    const addLayer = useAppStore((s) => s.addLayer);
    const updateLayer = useAppStore((s) => s.updateLayer);
    const deleteLayer = useAppStore((s) => s.deleteLayer);
    const setActiveLayer = useAppStore((s) => s.setActiveLayer);

    // VPLAYER columns: every viewport of the ACTIVE layout (paper space).
    const activeLayoutId = useLayoutStore((s) => s.activeId);
    const layouts = useLayoutStore((s) => s.layouts);
    const updateViewport = useLayoutStore((s) => s.updateViewport);
    const viewports = useMemo(
        () => layouts.find((l) => l.id === activeLayoutId)?.viewports ?? [],
        [layouts, activeLayoutId],
    );

    // Table state.
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState<SortState>({ col: 'name', dir: 'asc' });
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [anchorId, setAnchorId] = useState<string | null>(null);
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [statesOpen, setStatesOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    // Windowing state (measured viewport height + scroll offset).
    const scrollBodyRef = useRef<HTMLDivElement>(null);
    const [scrollTop, setScrollTop] = useState(0);
    const [viewportH, setViewportH] = useState(320);

    useEffect(() => {
        const el = scrollBodyRef.current;
        if (!el) return;
        const measure = () => setViewportH(el.clientHeight || 320);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    /** Contained entity counts per layer id (drives the delete warning). */
    const entityCountByLayer = useMemo(() => {
        const counts = new Map<string, number>();
        Object.values(entities).forEach((e) => {
            counts.set(e.layer, (counts.get(e.layer) ?? 0) + 1);
        });
        return counts;
    }, [entities]);

    const sortedLayers = useMemo(() => {
        const q = search.trim().toLowerCase();
        const list = Object.values(layers).filter((l) => !q || l.name.toLowerCase().includes(q));
        const dir = sort.dir === 'asc' ? 1 : -1;
        const key = (l: Layer): string | number => {
            switch (sort.col) {
                case 'name': return l.name.toLowerCase();
                case 'on': return l.visible ? 1 : 0;
                case 'freeze': return l.frozen ? 1 : 0;
                case 'lock': return l.locked ? 1 : 0;
                case 'plot': return l.plottable === false ? 0 : 1;
                case 'color': return l.color;
                case 'linetype': return (l.linetype ?? 'CONTINUOUS').toLowerCase();
                case 'lineweight': return typeof l.lineweight === 'number' ? l.lineweight : -1;
                case 'transparency': return typeof l.transparency === 'number' ? l.transparency : -1;
                default: return l.order;
            }
        };
        return list.sort((a, b) => {
            const ka = key(a), kb = key(b);
            if (ka < kb) return -1 * dir;
            if (ka > kb) return 1 * dir;
            return (a.order - b.order) * dir;
        });
    }, [layers, search, sort]);

    const toggleSort = useCallback((col: SortCol) => {
        setSort((s) => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }));
    }, []);

    // Windowing slice.
    const total = sortedLayers.length;
    const start = Math.max(0, Math.floor(scrollTop / ROW_H) - WINDOW_PAD);
    const end = Math.min(total, Math.ceil((scrollTop + viewportH) / ROW_H) + WINDOW_PAD);
    const visibleSlice = sortedLayers.slice(start, end);

    const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setScrollTop(e.currentTarget.scrollTop);
    }, []);

    // Row selection with shift-range (OCS multi-select).
    const selectRow = useCallback((id: string, shift: boolean) => {
        if (shift && anchorId) {
            const ids = sortedLayers.map((l) => l.id);
            const a = ids.indexOf(anchorId);
            const b = ids.indexOf(id);
            if (a >= 0 && b >= 0) {
                const [lo, hi] = a < b ? [a, b] : [b, a];
                setSelectedIds(ids.slice(lo, hi + 1));
                return;
            }
        }
        setSelectedIds([id]);
        setAnchorId(id);
    }, [anchorId, sortedLayers]);

    const addNewLayer = () => {
        const order = Object.keys(layers).length;
        let n = order + 1;
        // Name counter skips collisions (OCS "LayerN" scheme).
        const names = new Set(Object.values(layers).map((l) => l.name));
        while (names.has(`Layer${n}`)) n++;
        const id = `layer-${Date.now().toString(36)}-${n}`;
        addLayer({
            id,
            name: `Layer${n}`,
            // ACI 7 white (AutoCAD new-layer default), not a mid gray.
            color: '#ffffff',
            visible: true,
            locked: false,
            order,
            plottable: true,
        });
        setActiveLayer(id);
        setSelectedIds([id]);
        setRenamingId(id);
    };

    const doomedIds = selectedIds.filter((id) => id !== 'layer-0' && id !== activeLayerId && layers[id]);
    const canDelete = doomedIds.length > 0;

    const deleteSelected = () => {
        doomedIds.forEach((id) => deleteLayer(id));
        setSelectedIds([]);
        setConfirmDelete(false);
    };

    const toggleVpFreeze = useCallback((vpId: string, layerName: string) => {
        if (!activeLayoutId) return;
        const vp = viewports.find((v) => v.id === vpId);
        if (!vp) return;
        const frozen = vp.frozenLayers.includes(layerName);
        updateViewport(activeLayoutId, vpId, {
            frozenLayers: frozen
                ? vp.frozenLayers.filter((n) => n !== layerName)
                : [...vp.frozenLayers, layerName],
        });
    }, [activeLayoutId, viewports, updateViewport]);

    const headerCell = (col: SortCol, label: string, w: number) => (
        <Flex
            as="button"
            role="columnheader"
            aria-sort={sort.col === col ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
            align="center"
            justify="center"
            w={`${w}px`}
            h="24px"
            flexShrink={0}
            px={1}
            _hover={{ bg: 'bg.hover' }}
            onClick={() => toggleSort(col)}
            title={label}
        >
            <Text fontSize="10px" color="text.secondary" noOfLines={1} whiteSpace="nowrap">
                {label}{sort.col === col ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
            </Text>
        </Flex>
    );

    return (
        <Box h="full" display="flex" flexDirection="column" minW={0}>
            {/* Toolbar (OCS: New + Delete + Set Current + search + states) */}
            <Flex gap={1} px={2} py={2} align="center" flexWrap="nowrap" overflowX="auto" flexShrink={0}
                sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                <Tooltip label={t('ui.cad.layerManager.newLayer')} hasArrow>
                    <Button size="xs" variant="ghost" aria-label={t('ui.cad.layerManager.newLayer')}
                        onClick={addNewLayer} flexShrink={0} px={1}>
                        <Icon as={Plus} boxSize="14px" />
                    </Button>
                </Tooltip>
                <Tooltip label={t('ui.cad.layerManager.deleteLayer')} hasArrow>
                    <Button size="xs" variant="ghost" aria-label={t('ui.cad.layerManager.deleteLayer')}
                        onClick={() => setConfirmDelete(true)} isDisabled={!canDelete} flexShrink={0} px={1}>
                        <Icon as={Trash2} boxSize="14px" />
                    </Button>
                </Tooltip>
                <Tooltip label={t('ui.cad.layerManager.setCurrent')} hasArrow>
                    <Button size="xs" variant="ghost" aria-label={t('ui.cad.layerManager.setCurrent')}
                        onClick={() => { const id = selectedIds[0]; if (id && layers[id]) setActiveLayer(id); }}
                        isDisabled={selectedIds.length !== 1} flexShrink={0} px={1}>
                        <Icon as={Flag} boxSize="14px" />
                    </Button>
                </Tooltip>
                <Box flexShrink={1} minW="80px" flexBasis="130px">
                    <Input
                        size="xs"
                        variant="filled"
                        bg="surface.300"
                        color="text.primary"
                        placeholder={t('ui.cad.layerManager.search')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        minW={0}
                    />
                </Box>
                <Button size="xs" variant="ghost" flexShrink={0}
                    onClick={() => setStatesOpen(true)} whiteSpace="nowrap">
                    {t('ui.cad.layerManager.states')}
                </Button>
            </Flex>

            {/* Windowed rows: full-height spacer + absolutely positioned band
                (vertical windowing; horizontal scroll exposes the full frozen
                column set at narrow panel widths). */}
            <Box ref={scrollBodyRef} flex="1" minH="0" position="relative"
                overflowY="auto" overflowX="auto" onScroll={onScroll}>
                {/* Sticky table header (scrolls horizontally with the rows) */}
                <Box position="sticky" top={0} zIndex={1} bg="bg.toolbar" borderBottom="1px solid" borderColor="border.base" minW="max-content" w="full" flexShrink={0}>
                    <Flex w="max-content" minW="full" px={1}>
                        <Flex align="center" justify="center" w={`${COLS.status}px`} h="24px" flexShrink={0}
                            title={t('ui.cad.layerManager.statusCol')}>
                            <Box w="8px" h="8px" borderRadius="full" border="1px solid" borderColor="border.hover" />
                        </Flex>
                        {/* Name column header (flexes like the rows' name cell) */}
                        <Flex
                            as="button"
                            role="columnheader"
                            aria-sort={sort.col === 'name' ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                            align="center"
                            flex="1 1 auto"
                            minW="110px"
                            h="24px"
                            px={1}
                            _hover={{ bg: 'bg.hover' }}
                            onClick={() => toggleSort('name')}
                            title={t('ui.cad.layerManager.colName')}
                        >
                            <Text fontSize="10px" color="text.secondary" noOfLines={1} whiteSpace="nowrap">
                                {t('ui.cad.layerManager.colName')}{sort.col === 'name' ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                            </Text>
                        </Flex>
                        {headerCell('on', t('ui.cad.layerManager.colOn'), COLS.on)}
                        {headerCell('freeze', t('ui.cad.layerManager.colFreeze'), COLS.freeze)}
                        {headerCell('lock', t('ui.cad.layerManager.colLock'), COLS.lock)}
                        {headerCell('plot', t('ui.cad.layerManager.colPlot'), COLS.plot)}
                        {headerCell('color', t('ui.cad.layerManager.colColor'), COLS.color)}
                        {headerCell('linetype', t('ui.cad.layerManager.colLinetype'), COLS.linetype)}
                        {headerCell('lineweight', t('ui.cad.layerManager.colLineweight'), COLS.lineweight)}
                        {headerCell('transparency', t('ui.cad.layerManager.colTransparency'), COLS.transparency)}
                        {/* VPLAYER per-viewport freeze columns (active layout) */}
                        {viewports.map((vp, i) => (
                            <Flex key={vp.id} align="center" justify="center" w={`${COLS.vp}px`} h="24px" flexShrink={0}
                                title={t('ui.cad.layerManager.vpFreezeCol', { name: vp.title || `VP${i + 1}` })}>
                                <Text fontSize="9px" color="text.secondary" noOfLines={2} px={0.5}>
                                    {(vp.title || `VP${i + 1}`).slice(0, 6)}
                                </Text>
                            </Flex>
                        ))}
                    </Flex>
                </Box>
                <Box height={`${total * ROW_H}px`} position="relative" minW="max-content">
                    <Box position="absolute" top={`${start * ROW_H}px`} left={0} minW="max-content">
                        {visibleSlice.map((layer) => (
                            <LayerRow
                                key={layer.id}
                                layer={layer}
                                isActive={activeLayerId === layer.id}
                                isSelected={selectedIds.includes(layer.id)}
                                viewports={viewports}
                                activeLayoutId={activeLayoutId}
                                renaming={renamingId === layer.id}
                                onSelect={selectRow}
                                onRename={() => setRenamingId(layer.id)}
                                onRenameCommit={(name) => {
                                    const trimmed = name.trim();
                                    if (trimmed && trimmed !== layer.name) updateLayer(layer.id, { name: trimmed });
                                    setRenamingId(null);
                                }}
                                onUpdate={(updates) => updateLayer(layer.id, updates)}
                                onToggleVpFreeze={toggleVpFreeze}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>

            <LayerStatesDialog isOpen={statesOpen} onClose={() => setStatesOpen(false)} />

            <ConfirmDialog
                isOpen={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                onConfirm={deleteSelected}
                title={t('ui.cad.layerManager.deleteTitle')}
                message={t('ui.cad.layerManager.deleteMessage', { n: doomedIds.length })}
                confirmLabel={t('ui.cad.layerManager.deleteConfirm')}
                cancelLabel={t('common.cancel')}
                tone="destructive"
            />
        </Box>
    );
}

/** One table row: status marker, inline-rename name, toggles and style
 *  editors. Memoized + windowed so only the visible slice renders. */
const LayerRow = memo(function LayerRow({
    layer, isActive, isSelected, viewports, activeLayoutId,
    renaming, onSelect, onRename, onRenameCommit, onUpdate, onToggleVpFreeze,
}: {
    layer: Layer;
    isActive: boolean;
    isSelected: boolean;
    viewports: { id: string; frozenLayers: string[]; title: string }[];
    activeLayoutId: string | null;
    renaming: boolean;
    onSelect: (id: string, shift: boolean) => void;
    onRename: () => void;
    onRenameCommit: (name: string) => void;
    onUpdate: (updates: Partial<Layer>) => void;
    onToggleVpFreeze: (vpId: string, layerName: string) => void;
}) {
    const { t } = useTranslation();
    return (
        <Flex
            role="row"
            align="stretch"
            h={`${ROW_H}px`}
            w="max-content"
            minW="full"
            px={1}
            bg={isSelected ? 'bg.active' : 'transparent'}
            _hover={{ bg: 'bg.hover' }}
            onClick={(e) => onSelect(layer.id, e.shiftKey)}
            onDoubleClick={onRename}
        >
            {/* Status: the active-layer marker (OCS state icon column). */}
            <Flex align="center" justify="center" w={`${COLS.status}px`} flexShrink={0}
                title={isActive ? t('ui.cad.layerManager.activeLayer') : undefined}>
                {isActive && <Box w="8px" h="8px" borderRadius="full" bg="brand.500" />}
            </Flex>

            {/* Name (click renames inline; Enter/blur commits) */}
            <Flex align="center" flex="1 1 auto" minW="110px" px={1}>
                {renaming ? (
                    <Input
                        size="xs"
                        variant="filled"
                        bg="surface.300"
                        color="text.primary"
                        autoFocus
                        defaultValue={layer.name}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => onRenameCommit(e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === 'Enter') onRenameCommit(e.currentTarget.value);
                            if (e.key === 'Escape') onRenameCommit('');
                        }}
                    />
                ) : (
                    <Text fontSize="xs" color="text.primary" noOfLines={1} minW={0} whiteSpace="nowrap">
                        {layer.name}
                    </Text>
                )}
            </Flex>

            {/* On */}
            <CellToggle w={COLS.on} on={layer.visible} label={t('ui.cad.layerManager.toggleOn')}
                onClick={() => onUpdate({ visible: !layer.visible })}>
                <Icon as={layer.visible ? Eye : EyeOff} boxSize="13px" />
            </CellToggle>

            {/* Freeze (LAYFRZ: off + out of regen/snap) */}
            <CellToggle w={COLS.freeze} on={!!layer.frozen} label={t('ui.cad.layerManager.toggleFreeze')} tone="cyan.300"
                onClick={() => onUpdate({ frozen: !layer.frozen, visible: layer.frozen })}>
                <Icon as={CloudSnow} boxSize="13px" />
            </CellToggle>

            {/* Lock */}
            <CellToggle w={COLS.lock} on={layer.locked} label={t('ui.cad.layerManager.toggleLock')}
                onClick={() => onUpdate({ locked: !layer.locked })}>
                <Icon as={layer.locked ? Lock : Unlock} boxSize="13px" />
            </CellToggle>

            {/* Plot */}
            <CellToggle w={COLS.plot} on={layer.plottable !== false} label={t('ui.cad.layerManager.togglePlot')}
                onClick={() => onUpdate({ plottable: layer.plottable === false })}>
                <Icon as={Printer} boxSize="13px" />
            </CellToggle>

            {/* Color (ACI picker; layers carry concrete hex values) */}
            <Flex align="center" justify="center" w={`${COLS.color}px`} flexShrink={0} px={1}
                onClick={(e) => e.stopPropagation()}>
                <AciColorPicker value={layer.color} onCommit={(c) => {
                    if (c !== 'BYLAYER' && c !== 'BYBLOCK') onUpdate({ color: c });
                }} />
            </Flex>

            {/* Linetype */}
            <Flex align="center" w={`${COLS.linetype}px`} flexShrink={0} px={1} onClick={(e) => e.stopPropagation()}>
                <Select
                    size="xs"
                    variant="filled"
                    bg="surface.300"
                    color="text.primary"
                    value={layer.linetype ?? 'CONTINUOUS'}
                    onChange={(e) => onUpdate({ linetype: e.target.value })}
                    minW={0}
                >
                    {LINETYPES.map((lt) => <option key={lt} value={lt}>{lt}</option>)}
                </Select>
            </Flex>

            {/* Lineweight (27-value table + Default/ByLayer) */}
            <Flex align="center" w={`${COLS.lineweight}px`} flexShrink={0} px={1} onClick={(e) => e.stopPropagation()}>
                <Select
                    size="xs"
                    variant="filled"
                    bg="surface.300"
                    color="text.primary"
                    value={typeof layer.lineweight === 'number' ? String(layer.lineweight) : 'default'}
                    onChange={(e) => onUpdate({ lineweight: e.target.value === 'default' ? undefined : Number(e.target.value) })}
                    minW={0}
                >
                    <option value="default">{t('ui.cad.layerManager.defaultLw')}</option>
                    {LINEWEIGHTS.map((lw) => <option key={lw} value={lw}>{lw.toFixed(2)} mm</option>)}
                </Select>
            </Flex>

            {/* Transparency 0-100 */}
            <Flex align="center" w={`${COLS.transparency}px`} flexShrink={0} px={1} onClick={(e) => e.stopPropagation()}>
                <NumberInput
                    size="xs"
                    variant="filled"
                    bg="surface.300"
                    color="text.primary"
                    min={0}
                    max={100}
                    value={typeof layer.transparency === 'number' ? layer.transparency : 0}
                    onChange={(_, n) => onUpdate({ transparency: Number.isFinite(n) ? n : undefined })}
                    minW={0}
                >
                    <NumberInputField fontSize="xs" />
                </NumberInput>
            </Flex>

            {/* VPLAYER freeze columns (one per active-layout viewport) */}
            {viewports.map((vp) => (
                <CellToggle
                    key={vp.id}
                    w={COLS.vp}
                    on={vp.frozenLayers.includes(layer.name)}
                    label={t('ui.cad.layerManager.vpFreezeToggle', { name: vp.title || '' })}
                    tone="cyan.300"
                    onClick={() => activeLayoutId && onToggleVpFreeze(vp.id, layer.name)}
                >
                    <Icon as={CloudSnow} boxSize="12px" />
                </CellToggle>
            ))}
        </Flex>
    );
});
